import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import type { UseQueryWrapper } from '../../../types/useQuery.types';

import type { GetReportDataFilterEmployersParams } from '../../../Domain/Reports/Services/ReportService';
import { reportService } from '../../../Domain/Reports/Services/ReportService';

import { useSnakeBar } from '../../useSnakeBar';
import type { ReportEmployersResponse } from '../../../types/report.types';

type GetReportDataFilterEmployersQueryResultData = ReportEmployersResponse;

type GetReportDataFilterEmployersQueryVariables = {
  [Key in keyof GetReportDataFilterEmployersParams]: GetReportDataFilterEmployersParams[Key] | undefined;
};

const queryFn =
  ({ userId, page, limit, searchQuery }: GetReportDataFilterEmployersQueryVariables) =>
  async () => {
    if (userId) {
      const response = await reportService.getReportDataFilterEmployers({
        userId,
        page: page ?? 1,
        limit: limit ?? 25,
        searchQuery: searchQuery ?? undefined,
      });
      return (response as AxiosResponse).data;
    }
  };

const QUERY_KEY = 'getReportDataFilterEmployers';

export const useReportsGetDataFilterEmployersQuery: UseQueryWrapper<
  GetReportDataFilterEmployersQueryResultData,
  GetReportDataFilterEmployersQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<GetReportDataFilterEmployersQueryResultData>(
    [QUERY_KEY, variables.userId, variables.page, variables.limit, variables.searchQuery],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      ...(options || {}),
    },
  );
};
